import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Bookmark, ArrowRight, ChevronRight, Trash2 } from 'lucide-react';
import { useSaves } from '../../contexts/SavesContext';
import { useRegion } from '../../contexts/RegionContext';

const SavesPage = () => {
  const { savedItems, removeSave } = useSaves();
  const { formatPrice } = useRegion();

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] min-h-screen transition-colors duration-400">
      {/* Sub-header */}
      <div className="border-b border-[#d2d2d7] dark:border-white/[0.08]">
        <div className="max-w-[980px] mx-auto px-6 py-4">
          <h2 className="text-sm font-bold text-[#1d1d1f] dark:text-[#f5f5f7]">Your Saves</h2>
        </div>
      </div>

      <div className="max-w-[980px] mx-auto px-6">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="py-20 text-center"
        >
          <h1 className="text-[40px] md:text-[56px] font-bold tracking-tight leading-tight mb-5">
            Your Saves
          </h1>
          <p className="text-lg md:text-xl text-[#6e6e73] max-w-[600px] mx-auto leading-relaxed">
            Continue shopping the products you've previously saved. Share them with friends, family, and even FOZZIL Specialists to get help finding what's right for you.
          </p>
        </motion.div>

        {/* Divider */}
        <div className="border-t border-[#d2d2d7] dark:border-white/[0.08]" />

        {/* Saved Items or Empty State */}
        {savedItems.length > 0 ? (
          <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedItems.map((item) => (
              <div key={item.id} className="border border-[#d2d2d7] dark:border-white/[0.08] rounded-[32px] p-6 hover:shadow-lg transition-all group bg-white dark:bg-[#1c1c1e]">
                <div className="relative aspect-square mb-4 overflow-hidden rounded-2xl bg-[#f5f5f7] dark:bg-black/20 flex items-center justify-center p-6">
                  <img src={item.image} alt={item.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" />
                  <button 
                    onClick={() => removeSave(item.id)}
                    className="absolute top-3 right-3 p-2 rounded-full bg-white/80 dark:bg-black/50 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white dark:hover:bg-black"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-base font-semibold">{item.name}</h3>
                  <span className="text-sm font-bold text-[#065DC0]">{item.price}</span>
                </div>
                <p className="text-sm text-[#6e6e73] mb-6">{item.tagline}</p>
                <Link to={item.link} className="w-full py-2.5 rounded-full bg-[#f5f5f7] dark:bg-black/40 text-[#1d1d1f] dark:text-[#f5f5f7] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#065DC0] hover:text-white transition-all">
                  View Product <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="py-24 text-center"
          >
            <Bookmark size={48} strokeWidth={1} className="text-[#d2d2d7] dark:text-[#48484a] mx-auto mb-5" />
            <h2 className="text-[21px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7] mb-2">
              Save your first item.
            </h2>
            <Link
              to="/shop"
              className="text-[#065DC0] text-[15px] font-medium hover:underline inline-flex items-center gap-1"
            >
              Shop fozzil.in <ChevronRight size={14} />
            </Link>
          </motion.div>
        )}

        {/* Divider */}
        <div className="border-t border-[#d2d2d7] dark:border-white/[0.08]" />

        {/* How it works */}
        <div className="py-16">
          <h2 className="text-[28px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7] text-center mb-12">How Saves work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                icon: <Bookmark size={32} strokeWidth={1.5} />,
                title: 'Save products you love',
                desc: 'When browsing the FOZZIL Store, save any product to come back to later.',
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                ),
                title: 'Share your list',
                desc: 'Send your saves to friends, family, or a FOZZIL Specialist for expert advice.',
              },
              {
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1" />
                    <circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                ),
                title: 'Pick up where you left off',
                desc: 'Ready to buy? Add saved items directly to your bag with a single tap.',
              },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="text-[#86868b] dark:text-[#6e6e73] mx-auto mb-4 flex justify-center">
                  {step.icon}
                </div>
                <h3 className="text-[15px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7] mb-2">{step.title}</h3>
                <p className="text-[13px] text-[#6e6e73] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Encryption notice */}
        <div className="border-t border-[#d2d2d7] dark:border-white/[0.08] py-6 mb-8">
          <p className="text-xs text-[#86868b] text-center">
            The FOZZIL Online Store uses industry-standard encryption to protect the confidentiality of the information you submit.
            Learn more about our <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] hover:underline">Security Policy</Link>.
          </p>
        </div>        
      </div>
    </div>
  );
};

export default SavesPage;
